// Which figures lead the model bill, and which window they are about (module 10 U96, B675).
//
// THE FAULT THIS EXISTS TO PREVENT. Until 2026-09-20 these plates were today's in EVERY window:
// "spent today", then today's forecast, then what was left of today's ceiling, with the window's
// own total appended fifth. So picking 7 days or 30 days moved the total at the top of the page
// and left the three biggest figures beneath it sitting on today. Mike: *"when you select an
// increased period of 7 or 30 days, none of the data is updating apart from the total at the
// top."* The lanes, the models and the days below did change; the figures he was reading did not.
//
// It lives apart from the component so the rule can be tested as arithmetic rather than as a
// rendered page: the fault was never in the markup, it was in which number went where.
//
// A forecast of where today ends, and what is left of today's ceiling, are claims about NOW. They
// say nothing about a week, so on a longer window they are left out rather than shown stale --
// what is left of the ceiling keeps its own section at the foot of the page.
import type { UsageView } from './api.js';

export interface Plate {
  /** A dollar figure, or a word when the plate is a state rather than an amount ("Stopped"). */
  value: number | string;
  label: string;
  note: string;
  warn?: boolean;
}

/** Which stretch a figure covers, in the words a plate needs. */
export function windowWord(w: UsageView['window']): string {
  return w === 'today' ? 'today' : w === '7d' ? 'over seven days' : 'over thirty days';
}

export interface LeadFormat {
  usd: (v: number) => string;
  exact: (v: number) => string;
  plural: (n: number, one: string, many?: string) => string;
  clock: (iso: string) => string;
}

export function leadPlates(view: UsageView, resetsAt: string, f: LeadFormat): Plate[] {
  const { usd, exact, plural, clock } = f;
  const word = windowWord(view.window);
  const todayNote = `${plural(view.today.calls, 'call')} since ${clock(view.dayStart)}${view.today.failed > 0 ? `, ${exact(view.today.failed)} failed` : ''}`;

  if (view.window !== 'today') {
    const perDay = view.days.length > 0 ? view.totals.usd / view.days.length : 0;
    const plates: Plate[] = [{
      value: view.totals.usd,
      label: `spent ${word}`,
      note: `${plural(view.totals.calls, 'call')} over ${plural(view.days.length, 'day')}, ${usd(perDay)} a day${view.totals.failed > 0 ? `, ${exact(view.totals.failed)} of them failed` : ''}`,
    }];
    if (view.totals.wastedUsd > 0 && view.totals.usd > 0) {
      plates.push({
        value: view.totals.wastedUsd,
        label: `bought nothing ${word}`,
        warn: true,
        note: `${Math.round((view.totals.wastedUsd / view.totals.usd) * 100)}% of it. ${plural(view.totals.failed, 'call was', 'calls were')} refused, or came back in a shape Cobrain rejected. They still billed what they read.`,
      });
    }
    // Today is kept, last and plainly labelled, so the reading about NOW is not lost when the
    // window is long -- and so nobody mistakes the window's total for what is being spent now.
    plates.push({
      value: view.today.usd,
      label: 'of that, today',
      note: `${todayNote}. Today's own forecast and ceiling are on the Today view.`,
    });
    return plates;
  }

  const store = view.ceilings.find((c) => c.scope === 'store') ?? null;
  const plates: Plate[] = [
    { value: view.today.usd, label: 'spent today', note: todayNote },
    {
      value: view.today.forecastUsd,
      label: 'where today ends',
      note: `at the last three hours' rate, by ${resetsAt}. ${usd(view.today.lastFullHourUsd)} in the last full hour, ${usd(view.today.thisHourUsd)} in this one`,
    },
  ];
  if (store !== null) {
    plates.push({
      value: store.reached ? 'Stopped' : store.headroomUsd,
      label: store.reached ? `${store.name} is at its ceiling` : `left before ${store.name.toLowerCase()} stops`,
      note: `${usd(store.ceilingUsd)} a day, for the whole store`,
      warn: store.reached,
    });
  }
  if (view.totals.wastedUsd > 0) {
    plates.push({
      value: view.totals.wastedUsd,
      label: 'bought nothing today',
      warn: true,
      note: `${plural(view.totals.failed, 'call was', 'calls were')} refused, or came back in a shape Cobrain rejected. They still billed what they read.`,
    });
  }
  return plates;
}
